// AI Veri Analizi — Claude tabanlı sohbet ekranı.
// Sadece okuma — aktif firmaya ait tüm dönem verisi üzerinde analiz.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { Button, EmptyState } from '../components/ui';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:5001/api' : '/api';

// ─── Minimal markdown renderer (tablo, **bold**, `code`, ```block```) ───
function renderMarkdown(text) {
  if (!text) return null;
  // Code block (```...```)
  const blocks = [];
  let last = 0;
  const codeRe = /```(\w*)\n?([\s\S]*?)```/g;
  let m;
  while ((m = codeRe.exec(text)) !== null) {
    if (m.index > last) blocks.push({ type: 'text', value: text.slice(last, m.index) });
    blocks.push({ type: 'code', lang: m[1], value: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) blocks.push({ type: 'text', value: text.slice(last) });
  return blocks.map((b, i) => b.type === 'code'
    ? <pre key={i} className="ai-code-block"><code>{b.value}</code></pre>
    : <TextBlock key={i} text={b.value} />
  );
}

function TextBlock({ text }) {
  // Tabloları ayır
  const parts = [];
  const lines = text.split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Tablo başlangıcı: | col | col | + bir sonraki satır separator
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s\-:|]+\|\s*$/.test(lines[i + 1])) {
      const tableLines = [line, lines[i + 1]];
      let j = i + 2;
      while (j < lines.length && /^\s*\|.*\|\s*$/.test(lines[j])) {
        tableLines.push(lines[j]); j++;
      }
      parts.push({ type: 'table', value: tableLines });
      i = j;
    } else {
      parts.push({ type: 'p', value: line });
      i++;
    }
  }
  return parts.map((p, idx) => p.type === 'table' ? <MdTable key={idx} lines={p.value} /> : <Inline key={idx} text={p.value} />);
}

function MdTable({ lines }) {
  const cells = (line) => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(s => s.trim());
  const head = cells(lines[0]);
  const rows = lines.slice(2).map(cells);
  return (
    <div style={{ overflowX: 'auto', margin: '8px 0' }}>
      <table className="ai-md-table">
        <thead><tr>{head.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function Inline({ text }) {
  if (!text || !text.trim()) return <br />;
  // **bold**, `code`, basit linkler değil
  const parts = [];
  let buf = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end > -1) {
        if (buf) { parts.push(buf); buf = ''; }
        parts.push(<strong key={parts.length}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    } else if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > -1) {
        if (buf) { parts.push(buf); buf = ''; }
        parts.push(<code key={parts.length} className="ai-inline-code">{text.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  if (buf) parts.push(buf);
  return <p style={{ margin: '4px 0' }}>{parts}</p>;
}

// ─── Tool call/result blokları ───
function ToolBubble({ name, input, output, expanded, onToggle }) {
  const labels = { list_tables: 'Tabloları listele', describe_table: 'Tablo şeması', run_query: 'SQL sorgusu' };
  const label = labels[name] || name;
  return (
    <div className="ai-tool-bubble" onClick={onToggle}>
      <div className="ai-tool-header">
        <span className="ai-tool-icon">⚙</span>
        <span className="ai-tool-label">{label}</span>
        {input?.purpose && <span className="ai-tool-purpose"> — {input.purpose}</span>}
        {input?.table_name && <span className="ai-tool-purpose"> ({input.table_name})</span>}
        {output && output.row_count != null && <span className="ai-tool-meta">{output.row_count} satır{output.truncated ? ' (kırpılmış)' : ''}</span>}
        {output && output.error && <span className="ai-tool-meta" style={{ color: 'var(--danger)' }}>Hata</span>}
        <span className="ai-tool-toggle">{expanded ? '▾' : '▸'}</span>
      </div>
      {expanded && (
        <div className="ai-tool-body">
          {name === 'run_query' && input?.sql && (
            <pre className="ai-code-block"><code>{input.sql}</code></pre>
          )}
          {output && output.error && (
            <div style={{ color: 'var(--danger)', fontSize: '0.85rem', padding: 8 }}>{output.error}</div>
          )}
          {output && output.sample && output.sample.length > 0 && (
            <div style={{ fontSize: '0.78rem', padding: 8, color: 'var(--text-tertiary)' }}>
              <div style={{ marginBottom: 4 }}>İlk {output.sample.length} satır örneği:</div>
              <pre style={{ background: 'var(--bg-secondary)', padding: 8, borderRadius: 6, overflow: 'auto', maxHeight: 200 }}>
                {JSON.stringify(output.sample, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mesaj ───
function Message({ msg, expandedTools, toggleTool }) {
  if (msg.role === 'user') {
    return (
      <div className="ai-msg ai-msg-user">
        <div className="ai-msg-bubble"><Inline text={msg.content} /></div>
      </div>
    );
  }
  if (msg.role === 'assistant') {
    return (
      <div className="ai-msg ai-msg-assistant">
        <div className="ai-msg-avatar">AI</div>
        <div className="ai-msg-content">
          {msg.content && <div className="ai-msg-bubble">{renderMarkdown(msg.content)}</div>}
          {msg.tool_calls && msg.tool_calls.map((tc, i) => {
            const result = msg.tool_results_inline?.find(r => r.tool_use_id === tc.id);
            const k = msg.id + '-' + i;
            return <ToolBubble key={k} name={tc.name} input={tc.input} output={result?.output}
              expanded={!!expandedTools[k]} onToggle={() => toggleTool(k)} />;
          })}
        </div>
      </div>
    );
  }
  return null;
}

// ─── Ana sayfa ───
export default function AiAnalysisPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [aiSettings, setAiSettings] = useState(null);
  const [expandedTools, setExpandedTools] = useState({});
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get('/ai-settings');
      setAiSettings(r.data.data);
    } catch {}
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const r = await api.get('/ai-chat/conversations');
      setConversations(r.data.data || []);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) { setMessages([]); return; }
    try {
      const r = await api.get(`/ai-chat/conversations/${convId}/messages`);
      // Tool result'ları assistant mesajına inline ekle (UI kolaylığı için)
      const raw = r.data.data || [];
      const merged = [];
      for (let i = 0; i < raw.length; i++) {
        const m = raw[i];
        if (m.role === 'tool') {
          // Önceki assistant mesajına tool_results'ı bağla
          const prev = merged[merged.length - 1];
          if (prev && prev.role === 'assistant') {
            prev.tool_results_inline = (m.tool_results || []).map(tr => ({
              tool_use_id: tr.tool_use_id,
              output: tryParse(tr.content),
            }));
          }
        } else {
          merged.push({ ...m });
        }
      }
      setMessages(merged);
    } catch {}
  }, []);

  useEffect(() => { loadSettings(); loadConversations(); }, [loadSettings, loadConversations]);
  useEffect(() => { loadMessages(activeConvId); }, [activeConvId, loadMessages]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, statusText]);

  const newConversation = async () => {
    try {
      const r = await api.post('/ai-chat/conversations');
      await loadConversations();
      setActiveConvId(r.data.data.id);
      setMessages([]);
    } catch (err) { alert(err.response?.data?.message || 'Oluşturulamadı'); }
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu sohbeti silmek istiyor musunuz?')) return;
    try {
      await api.delete(`/ai-chat/conversations/${id}`);
      if (activeConvId === id) { setActiveConvId(null); setMessages([]); }
      await loadConversations();
    } catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  const toggleTool = (k) => setExpandedTools(p => ({ ...p, [k]: !p[k] }));

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    if (!aiSettings || !aiSettings.api_key_set) {
      setError('Önce Ayarlar → AI Asistan menüsünden sağlayıcı (Anthropic / Gemini) seçip API key girin.');
      return;
    }

    let convId = activeConvId;
    if (!convId) {
      try {
        const r = await api.post('/ai-chat/conversations');
        convId = r.data.data.id;
        setActiveConvId(convId);
        await loadConversations();
      } catch (err) { setError(err.response?.data?.message || 'Sohbet açılamadı'); return; }
    }

    // Optimistic: kullanıcı mesajını + boş asistan mesajını ekle
    const userMsg = { id: 'tmp-u-' + Date.now(), role: 'user', content: text, created_at: new Date() };
    const aiMsg = { id: 'tmp-a-' + Date.now(), role: 'assistant', content: '', tool_calls: [], tool_results_inline: [], created_at: new Date() };
    setMessages(prev => [...prev, userMsg, aiMsg]);
    setInput('');
    setSending(true);
    setError(null);
    setStatusText('Bağlanıyor...');

    const token = localStorage.getItem('erp_token');
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const resp = await fetch(`${API_BASE}/ai-chat/conversations/${convId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ content: text }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) {
        const errBody = await resp.text();
        throw new Error('HTTP ' + resp.status + ': ' + errBody.slice(0, 200));
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        for (const block of lines) {
          if (!block.startsWith('data:')) continue;
          const json = block.slice(5).trim();
          if (!json) continue;
          let evt;
          try { evt = JSON.parse(json); } catch { continue; }
          handleEvent(evt);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('İptal edildi');
      } else {
        setError(err.message);
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant' && !last.content) copy.pop();
          return copy;
        });
      }
    } finally {
      setSending(false);
      setStatusText('');
      abortRef.current = null;
      // Tam mesajları DB'den çek (sunucu kanonik sürüm)
      await loadMessages(convId);
      await loadConversations();
      await loadSettings();
    }
  };

  const handleEvent = (evt) => {
    if (evt.type === 'status') {
      setStatusText(evt.text || '');
    } else if (evt.type === 'text') {
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant') {
          last.content = (last.content || '') + (last.content ? '\n\n' : '') + evt.delta;
        }
        return copy;
      });
    } else if (evt.type === 'tool_use') {
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant') {
          last.tool_calls = [...(last.tool_calls || []), { id: 'live-' + Date.now(), name: evt.name, input: evt.input }];
        }
        return copy;
      });
      setStatusText(`${evt.name === 'run_query' ? 'SQL çalıştırılıyor' : evt.name === 'describe_table' ? 'Tablo şeması alınıyor' : 'Tablolar listeleniyor'}...`);
    } else if (evt.type === 'tool_result') {
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant' && last.tool_calls?.length) {
          const lastCall = last.tool_calls[last.tool_calls.length - 1];
          last.tool_results_inline = [...(last.tool_results_inline || []), { tool_use_id: lastCall.id, output: evt.output }];
        }
        return copy;
      });
    } else if (evt.type === 'error') {
      setError(evt.message);
    } else if (evt.type === 'done') {
      setStatusText('');
    }
  };

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const activeConv = conversations.find(c => c.id === activeConvId);

  return (
    <div className="ai-page">
      {/* Mobil üst başlık — hamburger + aktif sohbet adı */}
      <div className="ai-mobile-header">
        <button className="ai-menu-btn" onClick={() => setDrawerOpen(true)} title="Sohbetler">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <div className="ai-mobile-title">{activeConv?.title || 'AI Veri Analizi'}</div>
        <Button size="sm" variant="ghost" onClick={newConversation}>+</Button>
      </div>

      {/* Drawer backdrop (mobilde sidebar açıkken) */}
      <div className={'ai-backdrop' + (drawerOpen ? ' open' : '')} onClick={() => setDrawerOpen(false)} />

      {/* Sol panel — sohbet listesi */}
      <aside className={'ai-sidebar' + (drawerOpen ? ' open' : '')} onClick={(e) => {
        // Sohbet seçince mobilde drawer kapansın
        if (e.target.closest('.ai-conv-item') && !e.target.closest('.ai-conv-del')) setDrawerOpen(false);
      }}>
        <div className="ai-sidebar-header">
          <h3>AI Veri Analizi</h3>
          <Button size="sm" onClick={newConversation}>+ Yeni</Button>
        </div>
        <div className="ai-conv-list">
          {conversations.length === 0 ? (
            <div style={{ padding: 16, fontSize: '0.82rem', color: 'var(--text-tertiary)', textAlign: 'center' }}>Henüz sohbet yok</div>
          ) : conversations.map(c => (
            <div key={c.id}
                 className={'ai-conv-item' + (activeConvId === c.id ? ' active' : '')}
                 onClick={() => setActiveConvId(c.id)}>
              <div className="ai-conv-title">{c.title || 'Yeni sohbet'}</div>
              <div className="ai-conv-date">{new Date(c.updated_at).toLocaleDateString('tr-TR')}</div>
              <button className="ai-conv-del" onClick={(e) => deleteConversation(c.id, e)} title="Sil">×</button>
            </div>
          ))}
        </div>
        {aiSettings && (
          <div className="ai-sidebar-footer">
            <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginBottom: 2 }}>BU AY KULLANIM</div>
            <div style={{ fontSize: '0.85rem' }}>
              ${(aiSettings.monthly_spent_usd || 0).toFixed(4)} / ${aiSettings.monthly_limit_usd?.toFixed(2) || '50.00'}
            </div>
            {!aiSettings.api_key_set && (
              <div style={{ fontSize: '0.72rem', color: 'var(--warning)', marginTop: 6 }}>
                ⚠ API key tanımlı değil — Ayarlardan ekleyin
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Sağ ana alan */}
      <main className="ai-main">
        {!activeConvId ? (
          <div className="ai-welcome">
            <EmptyState
              title="AI Veri Analizi'ne hoş geldiniz"
              description="Soldan yeni bir sohbet başlatın. AI sadece okuma yapar — verilerinize zarar vermez."
            />
            <div className="ai-suggestions">
              <div className="ai-suggestions-title">Örnek sorular:</div>
              {[
                'Bu ay en çok satan 10 stok hangisi?',
                'En yüksek bakiyeli 5 cari hesabı listele',
                'Geçen ay banka bakiyem nasıl değişti?',
                'Vadesi gelen çek/senetlerimi göster',
                'Hangi stoklarda kritik seviyenin altında miyim?',
              ].map((q, i) => (
                <button key={i} className="ai-suggestion-chip" onClick={async () => {
                  await newConversation();
                  setInput(q);
                }}>{q}</button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="ai-messages" ref={scrollRef}>
              {messages.length === 0 && !sending && (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 32 }}>
                  Bir soru sorarak başla
                </div>
              )}
              {messages.map(m => (
                <Message key={m.id} msg={m} expandedTools={expandedTools} toggleTool={toggleTool} />
              ))}
              {sending && statusText && (
                <div className="ai-status">
                  <span className="ai-status-dot"></span>
                  {statusText}
                </div>
              )}
              {error && (
                <div className="ai-error">⚠ {error}</div>
              )}
            </div>
            <div className="ai-input-bar">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Sorunuzu yazın... (Enter: gönder, Shift+Enter: yeni satır)"
                rows={2}
                disabled={sending}
              />
              {sending ? (
                <Button variant="danger" onClick={stop}>Durdur</Button>
              ) : (
                <Button onClick={sendMessage} disabled={!input.trim()}>Gönder</Button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function tryParse(s) {
  try { return JSON.parse(s); } catch { return s; }
}
